import React, { useState, useRef, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserIcon } from '@heroicons/react/24/outline';
import MentionErrorBoundary from './MentionErrorBoundary';

const MentionRenderer = memo(({
  userId,
  displayName,
  className = '',
  onClick,
  showTooltip = true,
  disabled = false
}) => {
  const [showUserTooltip, setShowUserTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [userProfile, setUserProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const mentionRef = useRef(null);
  const tooltipTimeoutRef = useRef(null);
  const navigate = useNavigate();
  
  // Handle mention click
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (disabled) return;
    
    if (onClick) {
      onClick(userId, displayName);
    } else {
      // Default behavior: navigate to user profile
      navigate(`/network/${userId}`);
    }
  };
  
  // Handle mouse enter for tooltip
  const handleMouseEnter = async (e) => {
    if (!showTooltip || disabled) return;
    
    // Clear any existing timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    
    // Calculate tooltip position
    const rect = e.target.getBoundingClientRect();
    const position = {
      top: rect.bottom + 8,
      left: rect.left + (rect.width / 2)
    };
    
    // Adjust if tooltip would go off screen
    const tooltipWidth = 280;
    if (position.left + tooltipWidth / 2 > window.innerWidth - 10) {
      position.left = window.innerWidth - tooltipWidth - 10;
    } else if (position.left - tooltipWidth / 2 < 10) {
      position.left = 10;
    } else {
      position.left = position.left - tooltipWidth / 2;
    }
    
    setTooltipPosition(position);
    
    // Show tooltip after delay
    tooltipTimeoutRef.current = setTimeout(async () => {
      setShowUserTooltip(true);
      
      // Load user profile data if not already loaded
      if (!userProfile && !isLoading) {
        setIsLoading(true);
        try {
          // Import the service dynamically to avoid circular dependencies
          const { getUserProfileById } = await import('../../services/firebase/user-profile');
          const result = await getUserProfileById(userId, null);
          
          if (result.success) {
            setUserProfile(result.data);
          }
        } catch (error) {
          console.error('Error loading user profile for tooltip:', error);
        } finally {
          setIsLoading(false);
        }
      }
    }, 500); // 500ms delay before showing tooltip
  };
  
  // Handle mouse leave
  const handleMouseLeave = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    
    // Hide tooltip after short delay
    setTimeout(() => {
      setShowUserTooltip(false);
    }, 100);
  };
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);
  
  // Handle deleted/invalid users
  if (!userId || !displayName) {
    return (
      <span className={`text-gray-500 dark:text-gray-400 ${className}`}>
        {displayName || 'Unknown User'}
      </span>
    );
  }
  
  return (
    <MentionErrorBoundary fallbackType="mention">
      <span
        ref={mentionRef}
        className={`
          inline text-sm font-medium
          text-purple-600 dark:text-purple-400
          underline decoration-purple-600 dark:decoration-purple-400
          cursor-pointer transition-all duration-200 ease-in-out
          hover:text-purple-700 dark:hover:text-purple-300
          hover:decoration-purple-700 dark:hover:decoration-purple-300
          hover:bg-purple-50 dark:hover:bg-purple-900/20
          focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1
          rounded-sm px-0.5 -mx-0.5
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${className}
        `}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={`Mentioned user: ${displayName}`}
        data-user-id={userId}
        data-mention="true"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e);
          }
        }}
      >
        @{displayName}
      </span>
      
      {/* User Tooltip */}
      {showUserTooltip && showTooltip && (
        <MentionErrorBoundary fallbackType="inline">
          <UserTooltip
            userId={userId}
            userProfile={userProfile}
            isLoading={isLoading}
            position={tooltipPosition}
            onClose={() => setShowUserTooltip(false)}
          />
        </MentionErrorBoundary>
      )}
    </MentionErrorBoundary>
  );
});

// User tooltip component
const UserTooltip = ({ 
  userId, 
  userProfile, 
  isLoading, 
  position, 
  onClose 
}) => {
  const tooltipRef = useRef(null);
  
  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);
  
  return (
    <div
      ref={tooltipRef}
      className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-4 max-w-xs"
      style={{
        top: position.top,
        left: position.left,
        transform: 'translateX(-50%)'
      }}
      role="tooltip"
    >
      {isLoading ? (
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
          <div className="flex-1">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-2/3"></div>
          </div>
        </div>
      ) : userProfile ? (
        <div className="flex items-start space-x-3">
          {/* Profile Picture */}
          <div className="flex-shrink-0">
            {userProfile.basicInfo?.photoURL ? (
              <img
                src={userProfile.basicInfo.photoURL}
                alt={`${userProfile.basicInfo.firstName || 'User'}'s profile`}
                className="w-12 h-12 rounded-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className={`w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center ${
                userProfile.basicInfo?.photoURL ? 'hidden' : 'flex'
              }`}
            >
              <UserIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          
          {/* User Info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {userProfile.basicInfo?.firstName} {userProfile.basicInfo?.lastName}
            </p>
            
            {userProfile.basicInfo?.currentRole && (
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {userProfile.basicInfo.currentRole}
                {userProfile.basicInfo?.companyName && (
                  <span> at {userProfile.basicInfo.companyName}</span>
                )}
              </p>
            )}
            
            {userProfile.basicInfo?.location && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                📍 {userProfile.basicInfo.location}
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center">
          <UserIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            User profile not available
          </p>
        </div>
      )}
      
      {/* Tooltip arrow */}
      <div 
        className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full"
        style={{ marginTop: '-1px' }}
      >
        <div className="w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-200 dark:border-b-gray-600"></div>
        <div 
          className="w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-white dark:border-b-gray-800 absolute top-0 left-1/2 transform -translate-x-1/2"
          style={{ marginTop: '1px' }}
        ></div>
      </div>
    </div>
  );
};

MentionRenderer.displayName = 'MentionRenderer';

export default MentionRenderer;